/**
 * Product Gallery Component
 * Handles image carousel functionality with Swiper and thumbnail navigation
 */

class ProductGallery {
  constructor(container) {
    this.container = container;
    this.currentIndex = 0;
    this.images = [];
    this.thumbnails = [];
    this.swiper = null;
    this.zoomEnabled = true;
    this.isZoomed = false;
    this.isHovering = false;
    
    // Store bound event handlers for proper cleanup
    this.boundHandlers = {
      mouseEnter: this.handleMouseEnter.bind(this),
      mouseLeave: this.handleMouseLeave.bind(this),
      mouseMove: this.handleMouseMove.bind(this),
      thumbnailClick: this.handleThumbnailClick.bind(this)
    };
    
    this.init();
  }

  init() {
    // Prevent multiple initialization
    if (this.container.hasAttribute('data-gallery-initialized')) {
      console.warn('Gallery already initialized, skipping');
      return;
    }
    
    this.cacheElements();
    this.checkZoomSettings();
    this.waitForImagesToLoad().then(() => {
      this.initSwiper();
      this.bindEvents();
      
      // Mark as initialized
      this.container.setAttribute('data-gallery-initialized', 'true');
    });
  }

  cacheElements() {
    // Get all image elements (now inside swiper-zoom-container)
    this.images = Array.from(this.container.querySelectorAll('.swiper-zoom-container img'));
    
    // Get all thumbnail buttons
    this.thumbnails = Array.from(this.container.querySelectorAll('.product-gallery__thumbnail'));
  }

  checkZoomSettings() {
    // Check if zoom is enabled via data attribute
    const mainGallery = this.container.querySelector('.product-gallery__main');
    if (mainGallery) {
      const zoomEnabled = mainGallery.getAttribute('data-zoom-enabled');
      this.zoomEnabled = zoomEnabled === 'true';
    }
  }

  waitForImagesToLoad() {
    return new Promise((resolve) => {
      const images = this.container.querySelectorAll('.swiper-zoom-container img');
      
      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;
      
      // Check if images are already loaded
      const alreadyLoaded = Array.from(images).filter(img => img.complete && img.naturalHeight !== 0);
      if (alreadyLoaded.length === totalImages) {
        resolve();
        return;
      }

      const onImageLoad = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          resolve();
        }
      };

      const onImageError = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          resolve();
        }
      };

      // Add event listeners to images that haven't loaded yet
      images.forEach(img => {
        if (!img.complete || img.naturalHeight === 0) {
          img.addEventListener('load', onImageLoad, { once: true });
          img.addEventListener('error', onImageError, { once: true });
        } else {
          loadedCount++;
        }
      });

      // Fallback timeout in case some images never load
      setTimeout(() => {
        resolve();
      }, 3000);
    });
  }

  setupZoomHover() {
    if (!this.zoomEnabled) {
      return;
    }
    
    if (!this.swiper) {
      return;
    }

    const zoomContainers = this.container.querySelectorAll('.swiper-zoom-container');
    
    if (zoomContainers.length === 0) {
      return;
    }
    
    zoomContainers.forEach((container, index) => {
      // Remove any existing listeners to avoid duplicates
      container.removeEventListener('mouseenter', this.boundHandlers.mouseEnter);
      container.removeEventListener('mouseleave', this.boundHandlers.mouseLeave);
      container.removeEventListener('mousemove', this.boundHandlers.mouseMove);
      
      // Add hover event listeners using bound handlers
      container.addEventListener('mouseenter', this.boundHandlers.mouseEnter);
      container.addEventListener('mouseleave', this.boundHandlers.mouseLeave);
      container.addEventListener('mousemove', this.boundHandlers.mouseMove);
    });
  }

  handleMouseEnter(event) {
    this.isHovering = true;
  }

  handleMouseMove(event) {
    if (!this.isHovering || !this.zoomEnabled) return;
    
    // Get mouse position relative to the container
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // Get the image element
    const img = event.currentTarget.querySelector('img');
    if (!img) return;
    
    // Calculate the zoom center point (where the mouse is)
    const centerX = x;
    const centerY = y;
    
    // Apply custom zoom transform
    img.style.transformOrigin = `${centerX}px ${centerY}px`;
    img.style.transform = 'scale(2)';
    img.style.transition = 'transform 0.2s ease-out';
    
    this.isZoomed = true;
  }

  handleMouseLeave(event) {
    
    // Get the image element
    const img = event.currentTarget.querySelector('img');
    if (img) {
      img.style.transform = 'scale(1)';
      img.style.transition = 'transform 0.2s ease-out';
    }
    
    this.isZoomed = false;
    this.isHovering = false;
  }

  initSwiper() {
    const swiperContainer = this.container.querySelector('.product-gallery-swiper');
    if (!swiperContainer) {
      return;
    }

    // Check if Swiper is available
    if (typeof Swiper === 'undefined') {
      return;
    }

    // Check if Swiper is already initialized on this container
    if (swiperContainer.swiper) {
      console.warn('Swiper already initialized on this container');
      return;
    }

    try {
      this.swiper = new Swiper(swiperContainer, {
        // Basic settings
        loop: true,
        slidesPerView: 1,
        spaceBetween: 0,
        centeredSlides: true,
        
        // Disable Swiper's native zoom - we'll implement our own
        zoom: false,
        autoHeight: true,
        
        // Image loading settings
        preloadImages: false,
        lazy: {
          loadPrevNext: true,
          loadPrevNextAmount: 2,
        },
        
        // Navigation - use simple class selectors
        navigation: {
          nextEl: '.product-gallery-next',
          prevEl: '.product-gallery-prev',
        },
        
        // Pagination
        pagination: {
          el: '.product-gallery-pagination',
          clickable: true,
          dynamicBullets: true,
        },
        
        // Events
        on: {
          slideChange: (swiper) => {
            this.currentIndex = swiper.realIndex;
            this.updateThumbnails();
            this.updateAriaLabels();
          },
          init: (swiper) => {
            this.currentIndex = swiper.realIndex;
            this.updateThumbnails();
            
            // Force update after images are loaded
            setTimeout(() => {
              swiper.update();
              this.setupZoomHover();
            }, 100);
          }
        },
        
        // Touch/swipe settings
        touchRatio: 1,
        touchAngle: 45,
        grabCursor: true,
        
        // Keyboard control
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        
        // Accessibility
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous image',
          nextSlideMessage: 'Next image',
          firstSlideMessage: 'This is the first image',
          lastSlideMessage: 'This is the last image',
        }
      });
      
    } catch (error) {
      console.error('Error initializing Swiper:', error);
      this.swiper = null;
    }
  }

  bindEvents() {
    // Clean up existing thumbnail listeners first
    this.cleanupThumbnailListeners();
    
    // Thumbnail click events using bound handler
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', this.boundHandlers.thumbnailClick);
      // Store the index for the handler
      thumbnail.dataset.slideIndex = index;
    });
  }

  handleThumbnailClick(event) {
    const index = parseInt(event.currentTarget.dataset.slideIndex);
    this.goToSlide(index);
  }

  cleanupThumbnailListeners() {
    this.thumbnails.forEach((thumbnail) => {
      thumbnail.removeEventListener('click', this.boundHandlers.thumbnailClick);
    });
  }

  goToSlide(index) {
    if (!this.swiper || index < 0 || index >= this.images.length) {
      return;
    }
    
    // Use slideToLoop for loop mode, slideTo for normal mode
    if (this.swiper.params.loop) {
      this.swiper.slideToLoop(index);
    } else {
      this.swiper.slideTo(index);
    }
  }

  updateThumbnails() {
    // Update thumbnails
    this.thumbnails.forEach((thumbnail, index) => {
      if (index === this.currentIndex) {
        thumbnail.classList.add('product-gallery__thumbnail--active');
        thumbnail.setAttribute('aria-pressed', 'true');
      } else {
        thumbnail.classList.remove('product-gallery__thumbnail--active');
        thumbnail.setAttribute('aria-pressed', 'false');
      }
    });
  }

  updateAriaLabels() {
    if (!this.images || this.images.length === 0) return;
    
    const currentImage = this.images[this.currentIndex];
    const imageAlt = currentImage ? currentImage.alt || '' : '';
    
    // Update container aria-label
    this.container.setAttribute('aria-label', `Image gallery: ${imageAlt}`);
    
    // Update current image aria-label
    if (currentImage) {
      currentImage.setAttribute('aria-label', `Current image: ${imageAlt}`);
    }
  }

  // Public methods for external control
  getCurrentIndex() {
    if (this.swiper && typeof this.swiper.realIndex !== 'undefined') {
      return this.swiper.realIndex;
    }
    return this.currentIndex;
  }

  getTotalImages() {
    return this.images.length;
  }

  // Force update Swiper (useful after images load)
  updateSwiper() {
    if (this.swiper) {
      this.swiper.update();
    }
  }

  // Auto-play functionality (optional)
  startAutoPlay(interval = 5000) {
    if (!this.swiper) return;
    this.swiper.autoplay.start();
  }

  stopAutoPlay() {
    if (!this.swiper) return;
    this.swiper.autoplay.stop();
  }

  // Zoom control methods
  enableZoom() {
    this.zoomEnabled = true;
    this.setupZoomHover();
  }

  disableZoom() {
    this.zoomEnabled = false;
    // Reset all images to normal scale
    const images = this.container.querySelectorAll('.swiper-zoom-container img');
    images.forEach(img => {
      img.style.transform = 'scale(1)';
    });
  }

  zoomIn() {
    // This method can be used for programmatic zoom if needed
    console.log('Programmatic zoom in not implemented for custom zoom');
  }

  zoomOut() {
    // This method can be used for programmatic zoom if needed
    console.log('Programmatic zoom out not implemented for custom zoom');
  }

  toggleZoom() {
    // This method can be used for programmatic zoom if needed
    console.log('Programmatic toggle zoom not implemented for custom zoom');
  }

  // Destroy method for cleanup
  destroy() {
    // Clean up Swiper
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    
    // Clean up zoom hover listeners
    const zoomContainers = this.container.querySelectorAll('.swiper-zoom-container');
    zoomContainers.forEach((container) => {
      container.removeEventListener('mouseenter', this.boundHandlers.mouseEnter);
      container.removeEventListener('mouseleave', this.boundHandlers.mouseLeave);
      container.removeEventListener('mousemove', this.boundHandlers.mouseMove);
    });
    
    // Clean up thumbnail listeners
    this.cleanupThumbnailListeners();
    
    // Reset all zoom transforms
    const images = this.container.querySelectorAll('.swiper-zoom-container img');
    images.forEach(img => {
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = '';
      img.style.transition = '';
    });
    
    // Clear references
    this.images = [];
    this.thumbnails = [];
    this.container = null;
  }
}

// Initialize all product galleries on the page
document.addEventListener('DOMContentLoaded', () => {
  const galleryContainers = document.querySelectorAll('.product-gallery');
  
  galleryContainers.forEach((container, index) => {
    // Skip if already initialized
    if (container.hasAttribute('data-gallery-initialized')) {
      console.warn(`Gallery ${index} already initialized, skipping`);
      return;
    }
    
    // Initialize the gallery
    new ProductGallery(container);
  });
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductGallery;
}